import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useTheme } from '../theme';
import {
  COLUMN_1_ITEMS,
  COLUMN_2_ITEMS,
  GalleryProjectShowcase,
  GalleryScrollItem,
  getShowcaseProjectById,
} from './GalleryData';

const ArtworkImage: React.FC<{
  src: string;
  alt: string;
  className?: string;
  loading?: 'lazy' | 'eager';
}> = ({ src, alt, className, loading = 'lazy' }) => (
  <img
    src={src}
    alt={alt}
    className={className}
    loading={loading}
  />
);

/** Modal image: becomes visible on load (avoids global img[loading]{opacity:0} hiding it) */
const ModalImage: React.FC<{
  src: string;
  alt: string;
  className?: string;
}> = ({ src, alt, className }) => {
  const [loaded, setLoaded] = useState(false);
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      loading="eager"
      style={{ opacity: loaded ? 1 : 0, transition: 'opacity 0.25s ease' }}
      onLoad={() => setLoaded(true)}
    />
  );
};

export const ProjectArtworkModal: React.FC<{
  project: GalleryProjectShowcase;
  onClose: () => void;
}> = ({ project, onClose }) => {
  const { theme } = useTheme();
  const ui = {
    textPrimary: 'var(--theme-text-primary)',
    textSecondary: 'var(--theme-text-secondary)',
    textMuted: 'var(--theme-text-muted)',
    overlay: 'var(--theme-overlay)',
    border: theme.mood === 'light' ? 'rgba(17,24,39,0.14)' : 'rgba(255,255,255,0.14)',
    glass: theme.mood === 'light' ? 'rgba(255,255,255,0.72)' : 'rgba(8,10,14,0.72)',
  };

  const gallery = useMemo(
    () => (project.gallery.length > 0 ? project.gallery : [project.cover]),
    [project.cover, project.gallery]
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <motion.div
      role="dialog"
      aria-modal="true"
      aria-labelledby="project-modal-title"
      className="fixed inset-0 z-[120] p-4 sm:p-6 lg:p-8 flex items-center justify-center overflow-y-auto overscroll-contain"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      data-testid="project-artwork-modal"
    >
      <div
        className="absolute inset-0"
        style={{ backgroundColor: ui.overlay, opacity: 0.9 }}
        onClick={onClose}
        aria-label="Close modal"
        data-testid="modal-backdrop"
      />

      <motion.div
        className="relative w-full max-w-[1480px] h-[min(88dvh,860px)] rounded-3xl overflow-hidden border mx-auto my-auto"
        style={{
          borderColor: ui.border,
          backgroundColor: ui.glass,
          boxShadow: '0 30px 120px rgba(0,0,0,0.28)',
          backdropFilter: 'blur(18px) saturate(1.25)',
          WebkitBackdropFilter: 'blur(18px) saturate(1.25)',
        }}
        initial={{ opacity: 0, scale: 0.985, filter: 'blur(8px)' }}
        animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
        exit={{ opacity: 0, scale: 0.985, filter: 'blur(8px)' }}
        transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(circle at 18% 18%, rgba(244,160,39,0.16), transparent 36%)' }}
        />

        <button
          type="button"
          onClick={onClose}
          className="absolute top-5 right-5 z-20 flex h-10 w-10 items-center justify-center rounded-full border transition-opacity hover:opacity-70"
          style={{ borderColor: ui.border, color: ui.textPrimary, backgroundColor: theme.mood === 'light' ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.08)' }}
          aria-label="Close project modal"
          data-testid="modal-close-button"
        >
          <span className="material-symbols-outlined text-xl">close</span>
        </button>

        <div className="relative z-10 h-full p-5 sm:p-8 lg:p-10 flex flex-col">
          <div className="mb-5 sm:mb-7 pr-12">
            <h3
              id="project-modal-title"
              className="font-pixel text-[clamp(3rem,8vw,7.5rem)] font-black tracking-[-0.08em] leading-none"
              style={{
                color: 'transparent',
                WebkitTextFillColor: 'transparent',
                WebkitTextStroke: theme.mood === 'light' ? '1px rgba(17,24,39,0.56)' : '1px rgba(255,255,255,0.52)',
                backgroundImage: theme.mood === 'light'
                  ? 'linear-gradient(135deg, rgba(17,24,39,0.78), rgba(244,160,39,0.9), rgba(17,24,39,0.22))'
                  : 'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(244,160,39,0.9), rgba(255,255,255,0.18))',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                textShadow: '0 0 28px rgba(244,160,39,0.22)',
              }}
            >
              {project.title}
            </h3>
            <p className="mt-3 max-w-3xl text-sm sm:text-base" style={{ color: ui.textSecondary }}>
              {project.subtitle}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {project.technologies.map((technology) => (
                <span
                  key={`${project.id}-${technology}`}
                  className="rounded-full border px-3 py-1 text-[10px] font-mono uppercase tracking-[0.14em]"
                  style={{ borderColor: ui.border, color: ui.textMuted }}
                >
                  {technology}
                </span>
              ))}
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs sm:text-sm leading-relaxed" style={{ color: ui.textSecondary }}>
              <p>{project.concept}</p>
              <p>{project.clientNeed}</p>
              <p>{project.artisticNote}</p>
            </div>
          </div>

          <div className="grid flex-1 min-h-0 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 overflow-y-auto pr-1">
            {gallery.map((image, index) => (
              <motion.div
                key={`${project.id}-${image}-${index}`}
                initial={{ opacity: 0, y: 14, filter: 'blur(8px)' }}
                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1], delay: index * 0.025 }}
                className={index === 0 ? 'sm:col-span-2 sm:row-span-2 min-h-[260px]' : 'min-h-[170px]'}
              >
                <div className="relative h-full overflow-hidden rounded-2xl border" style={{ borderColor: ui.border }}>
                  <ModalImage
                    src={image}
                    alt={`${project.title} screenshot ${index + 1}`}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/58 via-black/8 to-transparent" />
                  <div className="absolute left-4 right-4 bottom-4 flex items-end justify-between gap-3 text-white">
                    <span className="text-sm sm:text-base font-semibold">{index === 0 ? project.title : `Screenshot ${index + 1}`}</span>
                    <span className="font-mono text-[10px] opacity-70">{index + 1}/{gallery.length}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Gallery Card Item - Similar to Works but adapted for scrolling
const GalleryCard: React.FC<{
  item: GalleryScrollItem;
  project: GalleryProjectShowcase;
  onOpenProject: (project: GalleryProjectShowcase) => void;
}> = ({ item, project, onOpenProject }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      type="button"
      onClick={() => onOpenProject(project)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="relative w-full aspect-[16/9] rounded-md sm:rounded-lg overflow-hidden shrink-0 border group text-left touch-manipulation"
      style={{ 
        borderColor: 'rgba(255,255,255,0.25)',
        backgroundColor: 'var(--theme-panel-bg)'
      }}
      data-testid={`gallery-card-${project.id}`}
    >
      {/* Image - Same approach as Works */}
      <motion.img
        src={project.cover}
        alt={project.title}
        className="w-full h-full object-cover"
        animate={{
          scale: isHovered ? 1.05 : 1
        }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        onError={(e) => {
          // Fallback to project cover if image fails
          const target = e.target as HTMLImageElement;
          if (target.src !== project.cover) {
            target.src = project.cover;
          }
        }}
      />

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-none" />

      {/* Content overlay */}
      <div className="absolute inset-0 flex flex-col justify-end p-3 sm:p-4 pointer-events-none">
        <p 
          className="font-mono text-[10px] uppercase tracking-[0.16em] text-white mb-1"
          style={{ textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}
        >
          {project.subtitle}
        </p>
        <div
          className="font-pixel text-white text-sm sm:text-base font-bold"
          style={{ textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}
        >
          {project.title}
        </div>
      </div>
    </button>
  );
};

// Scrolling Column Component
const ScrollingColumn: React.FC<{
  items: GalleryScrollItem[];
  direction?: 'up' | 'down';
  delay?: number;
  onOpenProject: (project: GalleryProjectShowcase) => void;
  paused?: boolean;
}> = ({ items, direction = 'up', delay = 0, onOpenProject, paused = false }) => {
  // Duplicate items for seamless infinite scroll
  const duplicatedItems = [...items, ...items];

  return (
    <div className="relative flex flex-col gap-3 sm:gap-4 md:gap-6 w-full overflow-hidden h-full min-h-0">
      <div
        className={`flex flex-col gap-3 sm:gap-4 md:gap-6 w-full ${direction === 'up' ? 'animate-scroll-up' : 'animate-scroll-down'}`}
        style={{ animationDelay: `${delay}s`, animationPlayState: paused ? 'paused' : 'running', willChange: 'transform' }}
      >
        {duplicatedItems.map((item, idx) => {
          const project = getShowcaseProjectById(item.projectId);
          if (!project) return null;

          return (
            <GalleryCard
              key={`${item.id}-${idx}`}
              item={item}
              project={project}
              onOpenProject={onOpenProject}
            />
          );
        })}
      </div>
    </div>
  );
};

// Main ScrollingGallery Component
const ScrollingGallery: React.FC = () => {
  const [selectedProject, setSelectedProject] = useState<GalleryProjectShowcase | null>(null);

  useEffect(() => {
    if (!selectedProject) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [selectedProject]);

  const closeProject = useCallback(() => setSelectedProject(null), []);

  return (
    <>
      <div
        className="h-full w-full grid grid-cols-2 gap-3 sm:gap-4 md:gap-6 overflow-hidden"
        style={{ pointerEvents: selectedProject ? 'none' : undefined }}
        aria-hidden={selectedProject ? true : undefined}
      >
        <div className="h-full min-h-0">
          <ScrollingColumn
            items={COLUMN_1_ITEMS}
            direction="up"
            onOpenProject={setSelectedProject}
            paused={!!selectedProject}
          />
        </div>
        <div className="h-full min-h-0">
          <ScrollingColumn
            items={COLUMN_2_ITEMS}
            direction="down"
            delay={-10}
            onOpenProject={setSelectedProject}
            paused={!!selectedProject}
          />
        </div>
      </div>

      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {selectedProject && (
            <ProjectArtworkModal
              project={selectedProject}
              onClose={closeProject}
            />
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default ScrollingGallery;
